import { deleteSink, fetchDataSet, pauseSink } from 'js/actions';
import DataSourceIcon from 'js/components/data_sources/DataSourceIcon';
import Menu from 'js/components/elements/menu/Menu';
import { ResourceConfig } from 'js/components/pipelines/cards/components/ResourceConfig';
import { ACTIVATE_SINK_REQUEST, GET_FULL_VENDOR_REQUEST } from 'js/constants/actionTypes';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useAPIUsage } from 'new/components/flows/types/genai_rag/context/APIUsage/useAPIUsage';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import { ReactComponent as DeleteIcon } from 'new/components/icons/close-cross.svg';
import { Button } from 'new/elements/Button';
import { ModalConfigureLLM } from 'new/elements/flows/ModalConfigureLLM';
import useLLMFields from 'new/elements/flows/ModalConfigureLLM/hooks/useLLMFields';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import useEntities from 'new/state/entities/repository/useEntities';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import useSinksAdapter from 'new/state/sinks/adapter/useSinksAdapter';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import React, { useEffect, useRef, useState } from 'react';
import { NodeBox } from '../components/NodeBox';
import { ResourceName } from '../components/ResourceName';
import classes from './LLMProcessor.module.scss';

interface QueryPromptProps {
  data: any;
}

export const LLMProcessor = (props: QueryPromptProps) => {
  const { data } = props;
  const dispatch = useAppDispatch();
  const meta = useResourceMeta(RESOURCE_TYPE_SINK, data.id, data.data_credentials);
  const { isEdit } = useDraftContext();
  const { activate } = useSinksAdapter();
  const isActivating = useIsLoading(ACTIVATE_SINK_REQUEST);
  const { refreshPipelineById } = usePipelinesAdapter();
  const tries = useRef(0);
  const { vendors } = useVendors();
  const { requestFullVendor } = useVendorsAdapter();
  const isLoading = useIsLoading(GET_FULL_VENDOR_REQUEST);

  const { setLLM, llm } = useAPIUsage();

  const { data: credential } = useEntities(`credentials[${data?.data_credentials_id}]`);

  const selectedConnection = credential?.vendor_id
    ? vendors[credential.vendor_id]?.name
    : credential?.connector?.type;

  const vendor = vendors[credential.vendor_id];

  const { filteredFields } = useLLMFields(selectedConnection);

  const [open, setOpen] = useState(false);

  const onOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (!filteredFields.length && !isLoading && vendor.config?.isLLM) {
      tries.current = tries.current + 1;
      requestFullVendor(selectedConnection);
    }
  }, [filteredFields, isLoading]);

  const { vendor_endpoint_id, ...templateData } = data?.template_config;

  const showCURL = () => {
    setLLM(data);
  };

  const onClickDelete = async () => {
    await dispatch(pauseSink(data.id, 'Deactivating LLM Processor'));
    await dispatch(deleteSink(data.id, 'Deleting LLM Processor'));

    if (data.data_set_id) {
      dispatch(fetchDataSet(data.data_set_id));
    }

    refreshPipelineById(data.origin_node_id);
  };

  return (
    <NodeBox data={data} className={classes.container}>
      <ResourceName
        {...props}
        hideActions
        RightView={
          <Menu
            horizontalDots
            items={[
              {
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: onClickDelete,
                dialogMessage: `Please confirm you want to delete this LLM Processor`,
                primaryButtonColor: 'error',
                discardLabel: 'Cancel',
                confirmLabel: 'Delete',
              },
            ]}
          />
        }
      >
        LLM PROCESSOR
      </ResourceName>
      <div className={classes.queryPromptContainer}>
        <Stack marginBlock="8px" gap="8px">
          <Stack direction="row" alignItems="center">
            <DataSourceIcon noBorder icon={meta.iconSm} />
            <NodeBox.Title>{meta?.name}</NodeBox.Title>
          </Stack>
          <Stack className={classes.configContainer}>
            <div className={classes.configTitle}>
              <Typography>LLM Config</Typography>
              <Button onClick={showCURL} color={'basic'} size="small" variant={'outlined'}>
                Show cURL
              </Button>
              {/*<CopyButton*/}
              {/*  tooltipTitle="Copy the cURL with the API info you can use to build your application"*/}
              {/*  title="Copy cURL"*/}
              {/*  toCopy={getCurl(apiKey.api_key, apiKey.url, {*/}
              {/*    llmConfig,*/}
              {/*    user_prompt: '<your-prompt-goes-here>'*/}
              {/*  })}*/}
              {/*/>*/}
            </div>
            <div className={classes.config}>
              <ResourceConfig label="Credential ID" value={data.data_credentials_id} />
              {Object.keys(templateData || {}).map((key) => {
                const field = filteredFields.find((field) => field.name === key);

                if (!field || field?.isHiddenField) {
                  return null;
                }

                return (
                  <ResourceConfig
                    key={key}
                    label={field?.display_name || key}
                    value={templateData?.[key]}
                  />
                );
              })}
            </div>
          </Stack>
          {isEdit && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
              <Button variant="outlined" size="small" onClick={onOpen}>
                Settings
              </Button>
              <ModalConfigureLLM
                data={data}
                dataSetId={data?.data_set_id}
                open={open}
                setOpen={setOpen}
              />
            </Stack>
          )}
        </Stack>
      </div>
    </NodeBox>
  );
};
